/* Resume Builder guided suggestions — "Build a professional resume in under 5 minutes."
   Reduce typing: select, then edit. Everything remains fully editable. */

export const OBJECTIVE_CATEGORIES = [
  {
    key: "fresher",
    label: "Fresher",
    objectives: [
      "Enthusiastic and quick-learning fresher seeking an entry-level opportunity to apply my knowledge, learn from experienced professionals and grow into a dependable team member.",
      "Motivated fresher with strong communication skills and a positive attitude, looking to start my career with an organization that values hard work and continuous learning.",
      "Recent graduate eager to contribute dedication, discipline and fresh ideas while building practical skills in a professional work environment.",
    ],
  },
  {
    key: "customer_support",
    label: "Customer Support",
    objectives: [
      "Customer-focused professional with strong communication and problem-solving skills, seeking a customer support role where I can deliver quick resolutions and great service experiences.",
      "Patient and empathetic support executive experienced in handling queries across voice and email, aiming to improve customer satisfaction and retention for a growing organization.",
      "Dedicated customer service professional committed to first-call resolution, clear communication and building long-term customer trust.",
    ],
  },
  {
    key: "sales",
    label: "Sales",
    objectives: [
      "Target-driven sales professional with proven persuasion and relationship-building skills, seeking a role where I can generate leads, close deals and consistently exceed targets.",
      "Energetic sales executive experienced in field and tele-sales, looking to grow revenue for the organization while advancing into a senior sales role.",
      "Result-oriented professional passionate about understanding customer needs and converting opportunities into long-term business relationships.",
    ],
  },
  {
    key: "telecaller",
    label: "Telecaller",
    objectives: [
      "Confident telecaller with a clear voice and strong Hindi and English communication, seeking a role where I can convert calls into satisfied customers.",
      "Experienced telecalling professional skilled at handling high call volumes, objection handling and maintaining accurate call records.",
      "Persuasive communicator looking for a telecalling opportunity to apply my listening skills, patience and target-focused approach.",
    ],
  },
  {
    key: "data_entry",
    label: "Data Entry",
    objectives: [
      "Detail-oriented data entry operator with fast, accurate typing skills, seeking a role where precision and timely delivery are valued.",
      "Organized professional experienced in MS Excel and data management tools, looking to support business operations with reliable and error-free data processing.",
      "Focused and disciplined operator committed to maintaining data accuracy, confidentiality and daily productivity targets.",
    ],
  },
  {
    key: "warehouse",
    label: "Warehouse",
    objectives: [
      "Hardworking warehouse executive experienced in inventory, packing and dispatch operations, seeking a role in a fast-paced logistics environment.",
      "Physically fit and process-driven professional committed to safe handling, accurate stock counts and on-time dispatches.",
      "Reliable team player looking to contribute to smooth warehouse operations while growing into a supervisory role.",
    ],
  },
  {
    key: "healthcare",
    label: "Healthcare",
    objectives: [
      "Compassionate healthcare professional dedicated to patient care, hygiene standards and supporting doctors in delivering quality treatment.",
      "Registered nursing professional with hands-on patient care experience, seeking a role in a reputed hospital to grow my clinical skills.",
      "Caring and responsible healthcare worker committed to patient comfort, accurate documentation and teamwork in demanding environments.",
    ],
  },
  {
    key: "banking",
    label: "Banking",
    objectives: [
      "Detail-oriented banking professional seeking a role in branch operations or sales where accuracy, compliance and customer trust are the priorities.",
      "Finance graduate passionate about banking products and customer relationship management, looking to build a long-term career in the BFSI sector.",
      "Process-driven professional experienced in KYC, account servicing and cross-selling, aiming to contribute to branch targets and service quality.",
    ],
  },
  {
    key: "it",
    label: "IT",
    objectives: [
      "Software professional skilled in modern programming languages and problem solving, seeking a role to build reliable, scalable applications.",
      "Passionate developer with hands-on project experience, looking to contribute clean code and continuous improvement to an engineering team.",
      "Technology enthusiast committed to learning new tools quickly and delivering quality software on schedule.",
    ],
  },
  {
    key: "general",
    label: "General Professional",
    objectives: [
      "Dedicated professional seeking a challenging role where I can apply my skills, take ownership of my work and contribute to the organization's growth.",
      "Sincere and adaptable team player looking for an opportunity to learn, perform and build a stable long-term career.",
      "Responsible professional committed to punctuality, quality work and continuous self-improvement.",
    ],
  },
];

export const QUALIFICATIONS = [
  "10th",
  "12th",
  "Diploma",
  "ITI",
  "Graduation",
  "Post Graduation",
  "MBA",
  "B.Tech",
  "B.Com",
  "BA",
  "MA",
];

export const JOB_TITLES = [
  "Customer Support Executive",
  "Sales Executive",
  "Telecaller",
  "Warehouse Executive",
  "Data Entry Operator",
  "Receptionist",
  "HR Executive",
  "Staff Nurse",
];

export const RESPONSIBILITY_SUGGESTIONS = {
  "Customer Support Executive": [
    "Handled customer queries over calls, chat and email with quick resolutions",
    "Maintained high CSAT scores and first-call resolution rates",
    "Recorded interactions accurately in the CRM system",
  ],
  "Sales Executive": [
    "Generated and followed up on leads to achieve monthly sales targets",
    "Built strong relationships with new and existing customers",
    "Negotiated and closed deals while maintaining healthy margins",
  ],
  Telecaller: [
    "Made 100+ outbound calls daily to prospective customers",
    "Explained products clearly and handled objections professionally",
    "Maintained daily call logs and follow-up schedules",
  ],
  "Warehouse Executive": [
    "Managed inward and outward inventory with accurate stock records",
    "Handled packing, labelling and on-time dispatch of orders",
    "Followed safety and material-handling procedures",
  ],
  "Data Entry Operator": [
    "Entered and verified large volumes of data with high accuracy",
    "Prepared daily MIS reports in MS Excel",
    "Maintained data confidentiality and met daily productivity targets",
  ],
  Receptionist: [
    "Greeted visitors and managed the front desk professionally",
    "Handled incoming calls, couriers and appointment scheduling",
    "Maintained visitor records and supported admin tasks",
  ],
  "HR Executive": [
    "Coordinated end-to-end recruitment including screening and interviews",
    "Managed onboarding, documentation and employee records",
    "Supported payroll inputs and employee engagement activities",
  ],
  "Staff Nurse": [
    "Provided bedside patient care and administered prescribed medication",
    "Monitored vitals and maintained accurate patient charts",
    "Assisted doctors during rounds and emergency procedures",
  ],
  _general: [
    "Completed assigned tasks accurately and on time",
    "Coordinated with team members to meet daily targets",
    "Maintained records and reported progress to the supervisor",
  ],
};

export const SKILL_CATEGORIES = [
  { key: "customer_support", label: "Customer Support", skills: ["Communication", "Customer Service", "CRM", "Voice Process", "Email Support"] },
  { key: "sales", label: "Sales", skills: ["Lead Generation", "Negotiation", "Sales Closing", "Relationship Building", "Cold Calling"] },
  { key: "warehouse", label: "Warehouse", skills: ["Inventory Management", "Packing", "Dispatch", "Stock Audit"] },
  { key: "healthcare", label: "Healthcare", skills: ["Patient Care", "Nursing", "First Aid", "Medical Documentation"] },
  { key: "office", label: "Office & Admin", skills: ["MS Excel", "MS Word", "Typing", "Data Entry", "Email Drafting", "Front Desk"] },
  { key: "it", label: "IT", skills: ["Python", "Java", "SQL", "React", "HTML/CSS", "JavaScript"] },
];

export const LANGUAGE_OPTIONS = [
  "Hindi",
  "English",
  "Marathi",
  "Gujarati",
  "Tamil",
  "Telugu",
  "Kannada",
  "Bengali",
];
